import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormGroup,
  ListItem,
  ListItemText,
  Paper,
  useTheme,
} from "@mui/material";
import { useReportContext } from "../../../ReportContext/useReportContext";
import { useTranslation } from "react-i18next";
import { parseDateToLgComun4 } from "../../../../../helpers/dateTransform";
import { IconHeader } from "../../../../Icon/IconHader/IconHeader";
import { updateStatusReport } from "../../../../../apis";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { EGCheckbox } from "../../../../MainComponents/Checkbox/EGCheckbox";
import { UTCHint } from "../../../../MainComponents";
import { useReportId } from "../../../../../context/report/ReportContext";

// Stamped with an explicit offset: the display helpers read a naive date as UTC
const nowForAPI = () => dayjs().toISOString();

export const DescriptionStatus = ({
  currentReportStatus,
  reportDetail,
  allChecked,
  setAllChecked,
  checkboxes,
  setCheckboxes,
  statuses,
  status,
}) => {
  // Custom Hooks
  const [t] = useTranslation("report");
  const theme = useTheme();
  const { status_locales } = useReportContext();
  const id = useReportId();
  const { username } = useSelector((state) => state.auth);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Initial states
  const [isLoading, setIsLoading] = useState(false);

  // Current report status
  const currentStatus = reportDetail?.status?.current;

  // Determine the source of checkboxes
  const latestHistoryEntry = reportDetail.status?.history
    ?.filter(
      (entry) =>
        entry.status === currentReportStatus && entry.sub_status?.length > 0
    )
    ?.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0];

  const normalizeKey = (k) => (k || "").toString().trim();

  // Get initial checkboxes
  const getInitialCheckboxes = () => {
    // base: objeto con keys tipo new_0_... -> { key, checked, ... }
    const baseObj =
      reportDetail?.status?.sub_status?.[0]?.[0]?.[currentStatus] || {};

    // We convert the base into a list (maintaining the order of Object.values).
    const baseList = Object.values(baseObj).map((v, idx) => ({
      id: v.id ?? `base-${idx}`,
      key: v.key ?? `item-${idx}`,
      checked: !!v.checked,
      update_at: v.update_at ?? v.updated_at ?? "",
      updated_by: v.updated_by ?? v.updated_by ?? "",
    }));

    // History (array) with the last time it was saved (may be empty)
    const historyList = latestHistoryEntry?.sub_status ?? [];

    // map by normalized key to overwrite
    const historyMap = {};
    historyList.forEach((h) => {
      const k = normalizeKey(h.key);
      historyMap[k] = {
        checked:
          typeof h.checked === "boolean" ? h.checked : !!historyMap[k]?.checked,
        update_at: h.update_at ?? h.updated_at ?? "",
        updated_by: h.updated_by ?? "",
        // if the history contains an ID, we use it; otherwise, the database ID will be retained or generated below

        id: h.id ?? null,
      };
    });

    const merged = baseList.map((item) => {
      const k = normalizeKey(item.key);
      if (historyMap[k]) {
        return {
          ...item,
          id: historyMap[k].id ?? item.id,
          checked: historyMap[k].checked,
          update_at: historyMap[k].update_at,
          updated_by: historyMap[k].updated_by,
        };
      }
      return item;
    });

    historyList.forEach((h, idx) => {
      const k = normalizeKey(h.key);
      const exists = merged.some((m) => normalizeKey(m.key) === k);
      if (!exists) {
        merged.push({
          id: h.id ?? `history-${idx}`,
          key: h.key,
          checked: !!h.checked,
          update_at: h.update_at ?? h.updated_at ?? "",
          updated_by: h.updated_by ?? "",
        });
      }
    });

    return merged;
  };

  // Load checkboxes on startup or when `reportDetail` changing
  useEffect(() => {
    const initialCheckboxes = getInitialCheckboxes();
    setCheckboxes(initialCheckboxes);
    setAllChecked(
      initialCheckboxes.length === 0 ||
        initialCheckboxes.every((item) => item.checked)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDetail, currentReportStatus]);

  // Synchronize `allChecked` when checkboxes change
  useEffect(() => {
    setAllChecked(
      checkboxes.length === 0 || checkboxes.every((item) => item.checked)
    );
  }, [checkboxes, setAllChecked]);

  // Send data
  const sendDataCheckboxes = async (updatedCheckboxes) => {
    setIsLoading(true);
    const updating = enqueueSnackbar(`${t("Care.updating")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      const isDenied = statuses.includes(status);
      if (currentStatus !== currentReportStatus) {
        enqueueSnackbar(t("Resolution.report_not_found") || "Status mismatch", {
          variant: "error",
          preventDuplicate: true,
        });
        navigate("/reports/all");
        throw new Error("Status mismatch");
      } else if (isDenied) {
        enqueueSnackbar(t("Resolution.report_not_found"), {
          variant: "error",
          preventDuplicate: true,
        });
        navigate("/reports/all");
        throw new Error("Denied status");
      }

      const dataToSend = {
        report_id: id,
        status: currentStatus,
        comment: "",
        message: "",
        checkboxes: updatedCheckboxes,
      };

      const resp = await updateStatusReport(dataToSend);

      if (resp?.message === "Updated Status") {
        enqueueSnackbar(t("Resolution.data_successfully_updated"), {
          variant: "success",
        });
        return resp;
      } else {
        // any unexpected response -> error for the caller to roll back
        const msg = resp?.message || "Unexpected response";
        throw new Error(msg);
      }
    } catch (error) {
      console.error("❌ Error to send data:", error);
      throw error;
    } finally {
      closeSnackbar(updating);
      setIsLoading(false);
    }
  };

  // Handle individual changes in checkboxes
  const handleCheckboxChange = (idKey) => {
    if (currentStatus !== currentReportStatus) return;

    setCheckboxes((prev) => {
      // clone the previous state so that it can be restored if it fails
      const prevClone = prev.map((p) => ({ ...p }));

      const updatedCheckboxes = prev.map((item) => {
        if (item.key === idKey) {
          return {
            ...item,
            checked: !item.checked,
            update_at: nowForAPI(),
            updated_by: username,
          };
        }
        return item;
      });

      // optimistic: we update the UI now and try to persist
      sendDataCheckboxes(updatedCheckboxes).catch((err) => {
        // rollback if fails
        console.warn("Rollback check due to error:", err);
        setCheckboxes(prevClone);
      });

      return updatedCheckboxes;
    });
  };

  // Handle “Select/Deselect all”
  const handleSelectAll = () => {
    if (currentStatus !== currentReportStatus) return;

    setCheckboxes((prev) => {
      const prevClone = prev.map((p) => ({ ...p }));
      const newCheckedState = !allChecked;
      const updatedCheckboxes = prev.map((item) => ({
        ...item,
        checked: newCheckedState,
        update_at: nowForAPI(),
        updated_by: username,
      }));

      sendDataCheckboxes(updatedCheckboxes).catch((err) => {
        console.warn("Rollback selectAll due to error:", err);
        setCheckboxes(prevClone);
        enqueueSnackbar(t("Resolution.error_updating") || "Error updating", {
          variant: "error",
        });
      });

      return updatedCheckboxes;
    });
  };

  let kindOfClosedOptions;

  if (localStorage.getItem("Tenant") == '"odoo_wwwlineaeticalive"') {
    // Status report type
    kindOfClosedOptions = [
      {
        label: "Cerrada Veraz",
        value: "founded",
      },
      {
        label: "Cerrada no Veraz ",
        value: "unfounded",
      },
      // {
      //   label: "Cerrada por falta de información/elementos",
      //   value: "inadmissible" },
      {
        label: "Cerrada por otros motivos",
        value: "undefined",
      },
    ];
  } else {
    // Status report type
    kindOfClosedOptions = [
      {
        label: t("Conclusion.closed_founded"),
        value: "founded",
      },
      {
        label: t("Conclusion.closed_unfounded"),
        value: "unfounded",
      },
      //{ label: t("Conclusion.inadmissible_report"), value: "inadmissible" },
      {
        label: t("Conclusion.closed_undetermined"),
        value: "undefined",
      },
    ];
  }

  const returnStatusLabel = () => {
    if (currentReportStatus !== "closed")
      return t(`status.${currentReportStatus}`);
    if (currentReportStatus === "closed") {
      if (!!reportDetail?.status?.details?.closed) {
        return kindOfClosedOptions.find(
          (item) => item.value === reportDetail.status.details.closed
        ).label;
      } else {
        return t(`status.closing_process`);
      }
    }
  };

  const returnStatusColor = () => {
    if (currentReportStatus === "closed" && !reportDetail?.status?.details) {
      return status_locales["closing_process"].color;
    }
    return status_locales[currentReportStatus].color;
  };

  return (
    <Paper
      elevation={0}
      className="dyBorder1 px-3 py-2"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexFlow: "row wrap",
        mt: "4vh",
        borderLeft: `10px solid ${returnStatusColor()}`,
        borderRadius: "6px",
      }}
    >
      <ListItem alignItems="flex-start" dense sx={{ p: 0 }}>
        <ListItemText
          primary={returnStatusLabel()}
          primaryTypographyProps={{ color: returnStatusColor(), variant: "h4" }}
          secondary={
            <>
              {`${t("Care.updated_at")}: ${
                reportDetail?.status?.details?.close_date
                  ? parseDateToLgComun4(
                      reportDetail.status.details.close_date,
                      t("Care.locale"),
                      true
                    )
                  : reportDetail?.status?.assigned
                  ? parseDateToLgComun4(
                      reportDetail.status.assigned,
                      t("Care.locale"),
                      true
                    )
                  : t("Care.no_updated")
              }`}
              {(reportDetail?.status?.details?.close_date ||
                reportDetail?.status?.assigned) && (
                <UTCHint
                  date={
                    reportDetail?.status?.details?.close_date ||
                    reportDetail?.status?.assigned
                  }
                />
              )}
              {reportDetail?.status?.user?.name && (
                <>
                  <br />
                  <strong>{t("Care.updated_by")}:</strong>{" "}
                  {reportDetail.status.user.name}
                </>
              )}
            </>
          }
        />
      </ListItem>

      {/* Task List */}
      {checkboxes.length > 0 && (
        <FormControl
          component="fieldset"
          sx={{ width: "100%", mt: 0.5, px: 0.5 }}
        >
          <IconHeader title={t("Care.sub_status")} />

          {checkboxes.length > 1 && currentStatus === currentReportStatus && (
            <Box px={1}>
              <EGCheckbox
                label={
                  allChecked ? t("Care.deselect_all") : t("Care.select_all")
                }
                checked={allChecked}
                onChange={handleSelectAll}
                disabled={isLoading || currentStatus !== currentReportStatus}
                color="primary"
                baseColor="secondary"
                checkedColorLabel="secondary"
                typographyProps={{ fontSize: 13 }}
              />
            </Box>
          )}
          <FormGroup
            sx={{
              display: "grid",
              px: 2,
              gridTemplateColumns: {
                xs: "1fr",
                lg: "1fr 1fr",
                xxl: "1fr 1fr 1fr",
              },
              gap: 0.5,
            }}
          >
            {checkboxes.map((task, i) => (
              <EGCheckbox
                label={task.key}
                key={`task-#${i + 1}`}
                checked={task.checked}
                onChange={() => handleCheckboxChange(task.key)}
                disabled={isLoading || currentStatus !== currentReportStatus}
                color="primary"
                checkedColorLabel="secondary"
                baseColor="secondary"
                typographyProps={{ fontSize: 13 }}
              />
            ))}
          </FormGroup>
        </FormControl>
      )}
    </Paper>
  );
};
